import { Box } from 'src/design/atoms/Box';
import { Paper } from 'src/design/atoms/Paper';
import React from 'react';
import { useOpenWeather } from 'react-open-weather';
import { JobDto } from 'src/api/FiretrolApiClient';
import { IWeatherData } from './WeatherCard.types';

export interface IWeatherCardProps {
  job: JobDto;
  onWeatherLoaded: (data: IWeatherData) => void;
  initialWeather: IWeatherData | null;
}

/** @registry Live-weather panel for a job's location (react-open-weather), falling back to a cached IWeatherData snapshot when provided. */
export const WeatherCard: React.FC<IWeatherCardProps> = ({
  job,
  onWeatherLoaded,
  initialWeather,
}) => {
  // Handle location which can be an object with geo coordinates or a string
  const locationObj = typeof job.location === 'object' ? job.location : null;
  const { data } = useOpenWeather({
    key: import.meta.env.VITE_OPEN_WEATHER_API_KEY,
    lat: locationObj?.geo?.lat,
    lon: locationObj?.geo?.lng,
    lang: 'en',
    unit: 'imperial', // values are (metric, standard, imperial)
  });
  React.useEffect(() => {
    if (!data || initialWeather) return;
    // react-open-weather ships no TypeScript types, so cast at the call site
    onWeatherLoaded(data as IWeatherData);
  }, [data]);

  // Render cached weather when provided, even before a live fetch resolves
  if (initialWeather) {
    return (
      <Paper p="md" withBorder>
        <Box ta="center">
          {initialWeather.current.temperature.current} Degrees -{' '}
          <span style={{ textTransform: 'capitalize' }}>
            {initialWeather.current.description}
          </span>{' '}
          - {initialWeather.current.wind} mph
        </Box>
      </Paper>
    );
  }
  if (!data) return null;
  return (
    <Paper p="md" withBorder>
      <Box m={0} ta="center">
        {data.current.temperature.current} Degrees -{' '}
        <span style={{ textTransform: 'capitalize' }}>
          {data.current.description}
        </span>{' '}
        - {data.current.wind} mph
      </Box>
    </Paper>
  );
};
